// ═══════════════════════════════════════════════════════════
// V·IANet Requests — FormSchemaRenderer
// Renderiza un JSON-schema de formulario a inputs dinamicos
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { request } from '../../../shared/services/api';

export interface FormComponent {
  type: string;
  key: string;
  label: string;
  validate?: {
    required?: boolean;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
  };
  options?: { value: string; label: string }[];
  attrs?: Record<string, unknown>;
  conditional?: Record<string, unknown>;
}

interface FormSchemaRendererProps {
  schema: { components: FormComponent[] };
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  readOnly?: boolean;
  errors?: Record<string, string>;
}

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'DM Sans, sans-serif',
  outline: 'none',
  background: '#FAFBFC',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
  display: 'block',
};

const ERROR_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: '#EF4444',
  marginTop: 2,
};

const READONLY_VALUE: React.CSSProperties = {
  fontSize: 13,
  color: '#1F2937',
  padding: '8px 0',
};

const UNSUPPORTED_TYPES = new Set(['peoplePicker', 'file', 'computed', 'signature']);

function FieldWrapper({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={LABEL_STYLE}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {error && <div style={ERROR_STYLE}>{error}</div>}
    </div>
  );
}

// ─── LOOKUP FIELD — Combobox con busqueda type-ahead ───

interface LookupOption {
  value: string;
  label: string;
}

function LookupField({
  comp,
  value,
  onChange,
  error,
  readOnly,
}: {
  comp: FormComponent;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  error?: string;
  readOnly?: boolean;
}) {
  const { t } = useI18n();
  const {
    endpoint,
    valueField = 'id',
    displayField = 'nombre',
  } = (comp.attrs ?? {}) as Record<string, string>;
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const fetchOptions = useCallback(() => {
    if (!endpoint) return;
    setLoading(true);
    setLoadError(false);
    request<{ data: Record<string, unknown>[] }>(endpoint)
      .then((res) => {
        const items = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res)
            ? (res as Record<string, unknown>[])
            : [];
        setOptions(
          items.map((item) => {
            const displayFields = displayField.split(',');
            const label = displayFields
              .map((f) => String(item[f.trim()] ?? ''))
              .join(' ')
              .trim();
            return {
              value: String(item[valueField] ?? ''),
              label: label || String(item[valueField] ?? ''),
            };
          }),
        );
      })
      .catch(() => {
        setOptions([]);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [endpoint, valueField, displayField]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Autofocus search when opening
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const selectedLabel = options.find((o) => o.value === String(value ?? ''))?.label;
  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (opt: LookupOption) => {
    onChange(comp.key, opt.value);
    setOpen(false);
    setSearch('');
    setHighlightIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(comp.key, null);
    setSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && filtered[highlightIndex]) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const req = comp.validate?.required;

  // readOnly mode — show label not ID
  if (readOnly) {
    const display = selectedLabel
      ? selectedLabel
      : loading
        ? (t.common.cargando ?? 'Cargando...')
        : value != null && value !== ''
          ? `${String(value)} (no encontrado)`
          : '—';
    return (
      <FieldWrapper label={comp.label}>
        <div style={READONLY_VALUE}>{display}</div>
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper label={comp.label} required={req} error={error}>
      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Trigger */}
        <div
          onClick={() => {
            setOpen(!open);
            setHighlightIndex(-1);
          }}
          style={{
            ...FIELD_STYLE,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            userSelect: 'none',
          }}
        >
          <span
            style={{
              flex: 1,
              color: selectedLabel ? '#1F2937' : '#9CA3AF',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? (t.common.cargando ?? 'Cargando...') : (selectedLabel ?? '— Seleccionar —')}
          </span>
          {value != null && value !== '' && (
            <button
              onClick={handleClear}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#9CA3AF',
                fontSize: 14,
                padding: 0,
                lineHeight: 1,
              }}
              title="Limpiar"
            >
              x
            </button>
          )}
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {/* Dropdown */}
        {open && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              background: 'white',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 16px rgba(31,56,100,0.12)',
              zIndex: 50,
              overflow: 'hidden',
            }}
          >
            {/* Search input */}
            <div
              style={{
                padding: 8,
                borderBottom: '1px solid #F3F4F6',
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  padding: '6px 10px',
                  background: '#FAFBFC',
                }}
              >
                <svg
                  width={13}
                  height={13}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setHighlightIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={`Buscar ${comp.label.toLowerCase()}...`}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: 12,
                    fontFamily: 'DM Sans, sans-serif',
                    flex: 1,
                    color: '#1F2937',
                  }}
                />
              </div>
            </div>

            {/* Options list */}
            <div ref={listRef} style={{ maxHeight: 240, overflowY: 'auto' }}>
              {loadError ? (
                <div
                  style={{
                    padding: '12px 16px',
                    fontSize: 12,
                    color: '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  Error al cargar opciones
                  <button
                    onClick={() => {
                      fetchOptions();
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#2F5496',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600,
                      textDecoration: 'underline',
                    }}
                  >
                    Reintentar
                  </button>
                </div>
              ) : filtered.length === 0 ? (
                <div
                  style={{
                    padding: '12px 16px',
                    fontSize: 12,
                    color: '#9CA3AF',
                    fontStyle: 'italic',
                  }}
                >
                  {loading ? (t.common.cargando ?? 'Cargando...') : 'Sin opciones disponibles'}
                </div>
              ) : (
                filtered.map((opt, i) => {
                  const isSelected = opt.value === String(value ?? '');
                  const isHighlighted = i === highlightIndex;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => handleSelect(opt)}
                      onMouseEnter={() => setHighlightIndex(i)}
                      style={{
                        padding: '8px 16px',
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: isHighlighted ? '#DBEAFE' : 'transparent',
                        borderLeft: isHighlighted ? '3px solid #33BCE1' : '3px solid transparent',
                        fontWeight: isSelected ? 600 : 400,
                        color: '#1F2937',
                        transition: 'background 0.1s',
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {opt.label}
                      </span>
                      {isSelected && (
                        <svg
                          width={14}
                          height={14}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#2F5496"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}

export function FormSchemaRenderer({
  schema,
  values,
  onChange,
  readOnly,
  errors,
}: FormSchemaRendererProps) {
  const { t } = useI18n();

  return (
    <div>
      {schema.components.map((comp) => {
        const val = values[comp.key];
        const err = errors?.[comp.key];
        const req = comp.validate?.required;

        if (UNSUPPORTED_TYPES.has(comp.type)) {
          return (
            <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
              <div
                style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', padding: '8px 0' }}
              >
                {t.requests.campoNoSoportado ?? 'Campo no soportado en esta version'}
              </div>
            </FieldWrapper>
          );
        }

        if (readOnly) {
          let display: string;
          if (comp.type === 'checkbox') {
            display = val ? 'Si' : 'No';
          } else if (comp.type === 'lookup') {
            // Lookup readOnly handled by LookupField component (resolves ID to label)
            return (
              <LookupField
                key={comp.key}
                comp={comp}
                value={val}
                onChange={onChange}
                error={err}
                readOnly
              />
            );
          } else if (comp.type === 'select' || comp.type === 'radio') {
            const opt = comp.options?.find((o) => o.value === val);
            display = opt?.label ?? String(val ?? '—');
          } else if (comp.type === 'currency') {
            const curr = (comp.attrs?.currency as string) ?? 'EUR';
            display = val != null ? `${Number(val).toLocaleString('es-ES')} ${curr}` : '—';
          } else {
            display = val != null && val !== '' ? String(val) : '—';
          }
          return (
            <FieldWrapper key={comp.key} label={comp.label}>
              <div style={READONLY_VALUE}>{display}</div>
            </FieldWrapper>
          );
        }

        // Editable mode
        switch (comp.type) {
          case 'text':
            if (comp.attrs?.multiline) {
              return (
                <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                  <textarea
                    value={String(val ?? '')}
                    onChange={(e) => onChange(comp.key, e.target.value)}
                    rows={Number(comp.attrs?.rows) || 3}
                    maxLength={comp.validate?.maxLength}
                    style={{ ...FIELD_STYLE, resize: 'vertical', minHeight: 60 }}
                  />
                </FieldWrapper>
              );
            }
            return (
              <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                <input
                  type="text"
                  value={String(val ?? '')}
                  onChange={(e) => onChange(comp.key, e.target.value)}
                  maxLength={comp.validate?.maxLength}
                  style={FIELD_STYLE}
                />
              </FieldWrapper>
            );

          case 'select':
            return (
              <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                <select
                  value={String(val ?? '')}
                  onChange={(e) => onChange(comp.key, e.target.value)}
                  style={{ ...FIELD_STYLE, cursor: 'pointer' }}
                >
                  <option value="">— Seleccionar —</option>
                  {comp.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </FieldWrapper>
            );

          case 'date':
            return (
              <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                <input
                  type="date"
                  value={String(val ?? '')}
                  onChange={(e) => onChange(comp.key, e.target.value)}
                  style={FIELD_STYLE}
                />
              </FieldWrapper>
            );

          case 'datetime':
            return (
              <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                <input
                  type="datetime-local"
                  value={String(val ?? '')}
                  onChange={(e) => onChange(comp.key, e.target.value)}
                  style={FIELD_STYLE}
                />
              </FieldWrapper>
            );

          case 'currency': {
            const curr = (comp.attrs?.currency as string) ?? 'EUR';
            return (
              <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>{curr}</span>
                  <input
                    type="number"
                    value={val != null ? String(val) : ''}
                    onChange={(e) =>
                      onChange(comp.key, e.target.value ? Number(e.target.value) : null)
                    }
                    min={comp.validate?.min}
                    max={comp.validate?.max}
                    step="0.01"
                    style={{ ...FIELD_STYLE, flex: 1 }}
                  />
                </div>
              </FieldWrapper>
            );
          }

          case 'checkbox':
            return (
              <FieldWrapper key={comp.key} label="" error={err}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#374151',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!val}
                    onChange={(e) => onChange(comp.key, e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#2F5496' }}
                  />
                  {comp.label}
                </label>
              </FieldWrapper>
            );

          case 'radio':
            return (
              <FieldWrapper key={comp.key} label={comp.label} required={req} error={err}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {comp.options?.map((opt) => (
                    <label
                      key={opt.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#374151',
                      }}
                    >
                      <input
                        type="radio"
                        name={comp.key}
                        value={opt.value}
                        checked={val === opt.value}
                        onChange={() => onChange(comp.key, opt.value)}
                        style={{ accentColor: '#2F5496' }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </FieldWrapper>
            );

          case 'lookup':
            return (
              <LookupField key={comp.key} comp={comp} value={val} onChange={onChange} error={err} />
            );

          default:
            return (
              <FieldWrapper key={comp.key} label={comp.label}>
                <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                  {t.requests.campoNoSoportado ?? `Tipo "${comp.type}" no soportado`}
                </div>
              </FieldWrapper>
            );
        }
      })}
    </div>
  );
}

// Validate all fields, return errors map
export function validateSchema(
  schema: { components: FormComponent[] },
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const comp of schema.components) {
    const val = values[comp.key];
    if (comp.validate?.required && (val == null || val === '')) {
      errors[comp.key] = 'Este campo es obligatorio';
    }
    if (
      comp.validate?.maxLength &&
      typeof val === 'string' &&
      val.length > comp.validate.maxLength
    ) {
      errors[comp.key] = `Maximo ${comp.validate.maxLength} caracteres`;
    }
    if (comp.validate?.min != null && typeof val === 'number' && val < comp.validate.min) {
      errors[comp.key] = `Valor minimo: ${comp.validate.min}`;
    }
  }
  return errors;
}
